'use strict';

import React, { useState } from 'react';
import route from 'shared/ziggy.js';
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { faComments } from "@fortawesome/pro-regular-svg-icons/faComments";
import { faDesktop } from "@fortawesome/pro-regular-svg-icons/faDesktop";
import { faSignOut } from "@fortawesome/pro-regular-svg-icons/faSignOut";
import { faStar } from "@fortawesome/pro-regular-svg-icons/faStar";
import { faToolbox } from "@fortawesome/pro-regular-svg-icons/faToolbox";
import { faUserSecret } from "@fortawesome/pro-regular-svg-icons/faUserSecret";
import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil';
import { postJson } from 'shared/fetch.js';

function AuthPanel(props) {
	if (props.loading) {
		return (
			<div className="m-4 antialiased">
				Loading…
			</div>
		);
	}
	
	return props.user
		? <UserPanel { ...props } />
		: <LoginPanel { ...props } />;
}

export function LoginPanel({ csrf, current_url, keepOpen = noop => noop, restoreDelay = noop => noop }) {
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [remember, setRemember] = useState(false);
	const [error, setError] = useState(null);
	const [status, setStatus] = useState('idle');
	
	let button_text = 'Log In';
	if ('logging-in' === status) {
		button_text = 'Please wait...';
	} else if ('logged-in' === status) {
		button_text = 'Logging in...';
	}
	
	const onSubmit = e => {
		e.preventDefault();
		
		setStatus('logging-in');
		
		const payload = { username, password };
		
		if (remember) {
			payload.remember = '1';
		}
		
		postJson(route('auth.login.store'), payload)
			.then(res => {
				if (res.redirected) {
					return Promise.resolve({
						ok: true,
						data: {
							destination: res.url,
						}
					});
				}
				
				return res.json();
			})
			.then(response => {
				if (response.ok) {
					window.location.href = response.data.destination;
					setStatus('logged-in');
					setError(null);
				} else {
					setStatus('idle');
					
					if ('username' in response.errors) {
						setError(response.errors.username[0]);
					} else if ('password' in response.errors) {
						setError(response.errors.password[0]);
					} else {
						setError(`Invalid username or password.`);
					}
				}
			});
	};
	
	return (
		<div className="m-4 antialiased text-left">
			<form action={ route('auth.login.store') } method="post" onSubmit={ onSubmit }>
				<input type="hidden" name="_token" value={ csrf } />
				<input type="hidden" name="redirect" value={ current_url } />
				
				<div className="mb-4 text-lg text-center text-gray-400 font-condensed" style={ { display: error ? 'none' : 'block' } }>
					Log into your account
				</div>
				
				<div className="alert alert-red" style={ { display: error ? 'block' : 'none' } }>
					{ error }
				</div>
				
				<div className="mb-4">
					<label className="inline-block mb-1 font-semibold" htmlFor="navbar--login-username">
						Your username or email:
					</label>
					<input
						value={ username }
						onChange={ e => setUsername(e.target.value) }
						onFocus={ e => keepOpen() }
						onBlur={ e => restoreDelay() }
						type="text"
						name="username"
						id="navbar--login-username"
						className="block p-2 w-full text-base leading-normal text-gray-700 bg-white rounded border border-gray-300 focus:outline-none focus:ring"
					/>
				</div>
				
				<div className="mb-4">
					<label className="inline-block mb-1 font-semibold" htmlFor="navbar--login-password">
						Your password:
					</label>
					<input
						value={ password }
						onChange={ e => setPassword(e.target.value) }
						onFocus={ e => keepOpen() }
						onBlur={ e => restoreDelay() }
						type="password"
						name="password"
						id="navbar--login-password"
						className="block p-2 w-full text-base leading-normal text-gray-700 bg-white rounded border border-gray-300 focus:outline-none focus:ring"
					/>
				</div>
				
				<div className="mb-4">
					<label>
						<input
							type="checkbox"
							name="remember"
							checked={ remember }
							onChange={ e => setRemember(e.target.checked) }
							id="navbar--login-remember"
						/>
						<span className="inline-block ml-1">Remember me</span>
					</label>
				</div>
				
				<div className="flex justify-between items-center mt-8">
					<button className={ `btn font-bold ${ 'logging-in' === status ? 'btn-gray' : 'btn-green' }` } type="submit">
						{ button_text }
					</button>
					<a className={ `${ 'idle' === status ? '' : 'hidden' } text-sm font-condensed text-blue-300 hover:text-blue-500` } href={ route('auth.forgot-password.create') }>
						Forgot your password?
					</a>
				</div>
			</form>
			
			<div className="pt-6 mt-6 text-sm text-center text-gray-600 border-t">
				Not a member yet? <a href={ route('join') }>Sign up.</a>
			</div>
		</div>
	);
}

export function UserPanel({ user, admin_content, article_key, csrf }) {
	const { given_name, is_paying_member, is_all_access, is_staff, role, photo, display_name, impersonator_id } = user;
	
	return (
		<div className="m-4 text-left">
			<div className="flex items-center mb-8">
				<a href={ route('my.dashboard.index') }
				   style={ {
					   background: `url(${ photo }) no-repeat center center`,
					   width: 80,
					   height: 80,
				   } }
				   className="bg-cover rounded-full border hover:border-blue-500"
				/>
				<div className="pl-4">
					<div className="text-lg font-bold font-condensed">
						{ display_name }
					</div>
					<div className="mt-px">
						<span className="py-1 px-4 text-xs text-gray-700 bg-gray-200 rounded-full">
							{ role }
						</span>
					</div>
				</div>
			</div>
			<ul className="antialiased text-left list-reset">
				{ false === is_paying_member && (
					<li className="my-2 text-lg font-condensed">
						<a href={ route('my.billing') } className="block text-gray-600 no-underline hover:text-blue">
							<Icon icon={ faStar } className="inline mr-2 svg-fw" />
							Upgrade Account
						</a>
					</li>
				) }
				<li className="my-2 text-lg font-condensed">
					<a href={ route('my.dashboard.index') } className="block text-gray-600 no-underline hover:text-blue">
						<Icon icon={ faDesktop } className="inline mr-2 svg-fw" />
						My Dashboard
					</a>
				</li>
				{ (is_all_access || is_staff) && (
					<li className="my-2 text-lg font-condensed">
						<a href="https://forum.nachi.org/c/members-only" className="block text-gray-600 no-underline hover:text-blue">
							<Icon icon={ faComments } className="inline mr-2 svg-fw" />
							Members-Only Forum
						</a>
					</li>
				) }
				{ is_staff && (
					<li className="pt-2 my-2 mt-4 border-t">
						
						<a href={ route('admin.dashboards.index') } className="block my-2 text-lg text-gray-600 no-underline font-condensed hover:text-blue">
							<Icon icon={ faToolbox } className="inline mr-2 svg-fw" />
							Admin Dashboard
						</a>
						
						{ article_key && (
							<a href={ route('admin.articles.edit', article_key) } className="block my-2 text-lg text-gray-600 no-underline font-condensed hover:text-blue">
								<Icon icon={ faPencil } className="inline mr-2 svg-fw" />
								Edit This Page
							</a>
						) }
						
						{ admin_content && <div dangerouslySetInnerHTML={ { __html: admin_content } } /> }
						
						<form className="my-4" action={ route('admin.users.search.index') } method="get">
							<input
								name="query"
								className="block py-1 px-3 w-full font-sans text-sm font-normal leading-normal text-gray-700 bg-white rounded-full border border-gray-300 focus:outline-none focus:ring"
								placeholder="Press ENTER to search"
								autoComplete="off"
							/>
						</form>
					
					</li>
				) }
				<li className="pt-4 my-2 mt-4 text-lg border-t font-condensed">
					{ impersonator_id && (
						<div className="my-2">
							<a href={ route('admin.stop-impersonating') } className="block text-gray-600 no-underline hover:text-blue">
								<Icon icon={ faUserSecret } className="inline mr-2 svg-fw" />
								Stop Viewing as { given_name }
							</a>
						</div>
					) }
					<form action={ route('logout') } method="get">
						<input type="hidden" name="_token" value={ csrf } />
						<button className="block p-0 m-0 text-gray-600 no-underline bg-transparent hover:text-blue" type="submit">
							<Icon icon={ faSignOut } className="inline mr-2 svg-fw" />
							Log Out
						</button>
					</form>
				</li>
			</ul>
		</div>
	);
}

export default AuthPanel;
